<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="800" scrollable>
    <v-card min-height="400">
      <v-card-title>
        <template v-if="!editingName">
          {{ meal.name || mealName }}
          <v-btn icon small class="ml-1" title="Rename meal" @click="startEditName">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
        </template>
        <template v-else>
          <v-text-field
            v-model="nameEdit"
            label="Meal name"
            hide-details
            autofocus
            @keyup.enter="saveName"
          />
          <v-btn icon color="success" :loading="savingName" @click="saveName">
            <v-icon>mdi-check</v-icon>
          </v-btn>
          <v-btn icon @click="editingName=false">
            <v-icon>mdi-undo</v-icon>
          </v-btn>
        </template>
        <v-spacer/>
        <v-btn text small class="mr-2" @click="toggleHistory">
          <v-icon small left>mdi-history</v-icon>
          {{ showHistory ? 'Back' : 'Restore' }}
        </v-btn>
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle>
        {{ formatDate(meal.date) }}
        <v-chip v-if="dietName" class="ml-2" x-small outlined>{{ dietName }}</v-chip>
        <router-link class="ml-2" :to="{name:'MealDesign', params:{id: mealId}}">M{{ mealId }}</router-link>
      </v-card-subtitle>
      <v-card-text v-if="showHistory && preview">
        <v-alert type="warning" outlined dense>
          This is the meal as it was before the change made
          {{ formatEditDate(preview.edit.created_at) }} by {{ preview.edit.user_email || 'unknown' }}<span
            v-if="preview.edit.screen"> ({{ preview.edit.screen }})</span>.
          Restoring will replace the current recipe with this version.
        </v-alert>
        <div class="mb-1">
          <v-btn small outlined class="mr-2" @click="preview=null">
            <v-icon small left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
          <v-btn
            small color="primary"
            :loading="restoringEditId === preview.edit.id"
            @click="confirmRestore"
          >
            <v-icon small left>mdi-history</v-icon>
            Restore This Version
          </v-btn>
        </div>
        <p class="caption mb-3">
          Differences from the current meal are highlighted:
          <b>bold</b> = different or added back,
          <span class="text-decoration-line-through red--text">struck through</span> = will be removed.
        </p>
        <h3>
          {{ preview.meal.name }}
          <span v-if="preview.meal.name !== meal.name" class="caption orange--text">
            currently: {{ meal.name }}
          </span>
        </h3>
        <div class="caption">
          {{ formatDate(preview.meal.date) }}
          <span v-if="previewDateDiffers" class="orange--text">currently: {{ formatDate(meal.date) }}</span>
        </div>
        <p v-if="preview.meal.description || meal.description">
          <span :style="previewDescriptionDiffers ? 'font-weight: bolder' : ''">{{ preview.meal.description }}</span>
          <span v-if="previewDescriptionDiffers" class="caption orange--text">(description differs)</span>
        </p>
        <v-row>
          <v-col cols="6">
            <h4>Ingredients</h4>
            <div v-for="group of previewGroups" v-bind:key="group.name" class="mt-3">
              <strong>{{ group.name }}</strong>
              <span class="caption ml-1">{{ formatWeightWithUnits(group.total) }}</span>
              <ul>
                <li
                  v-for="ingredient of group.ingredients" v-bind:key="`${ingredient.foodid}-${ingredient.status}`"
                  :style="ingredient.status==='added' || ingredient.status==='changed' ? 'font-weight: bolder' : ''"
                >
                  <span :class="ingredient.status==='removed' ? 'text-decoration-line-through red--text' : ''">
                    {{ Number(ingredient.amount).toFixed(1) }}g {{ ingredient.name }}
                  </span>
                  <span v-if="ingredient.status==='changed'" class="caption orange--text">
                    (currently {{ Number(ingredient.currentAmount).toFixed(1) }}g)
                  </span>
                  <span v-else-if="ingredient.status==='added'" class="caption green--text">
                    (not in current recipe)
                  </span>
                </li>
              </ul>
            </div>
          </v-col>
          <v-col cols="6">
            <h4>Instructions</h4>
            <ol class="mt-3">
              <li
                v-for="(instruction,i) of previewInstructions" v-bind:key="i"
                :style="instruction.status==='changed' ? 'font-weight: bolder' : ''"
              >
                <span :class="instruction.status==='removed' ? 'text-decoration-line-through red--text' : ''">
                  {{ instruction.text }}
                </span>
              </li>
            </ol>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-text v-else-if="showHistory">
        <v-progress-linear indeterminate v-if="loadingEdits"/>
        <p v-if="!loadingEdits && edits.length===0" class="caption mt-3">No recorded edits for this meal.</p>
        <v-simple-table v-if="edits.length>0" dense>
          <thead>
          <tr>
            <th>When</th>
            <th>Who</th>
            <th>Screen</th>
            <th>Changes</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="edit of edits" v-bind:key="edit.id">
            <td style="white-space: nowrap">{{ formatEditDate(edit.created_at) }}</td>
            <td>{{ edit.user_email || 'unknown' }}</td>
            <td>{{ edit.screen || '' }}</td>
            <td>
              <span :title="changeDetail(edit)">{{ changeSummary(edit) }}</span>
            </td>
            <td>
              <v-btn
                x-small outlined
                :loading="loadingPreview === edit.id"
                :title="`Preview the meal as it was before this change`"
                @click="openPreview(edit)"
              >
                Restore...
              </v-btn>
            </td>
          </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
      <v-card-text v-else>
        <v-progress-linear indeterminate v-if="loading"/>
        <p v-if="meal.description">{{ meal.description }}</p>
        <v-row>
          <v-col cols="6">
            <h3>
              Ingredients
              <span v-if="mealCost.hasAnyPrice" class="ml-1">${{ formatDollars(mealCost.cost) }}</span>
            </h3>
            <div v-if="mealCost.unpriced.length>0" class="caption orange--text">
              cost excludes {{ mealCost.unpriced.length }} unpriced:
              {{ mealCost.unpriced.map(i => i.name).join(', ') }}
            </div>
            <div v-for="group of ingredientGroups" v-bind:key="group.key" class="mt-3">
              <strong>{{ group.name }}</strong>
              <span class="caption ml-1">{{ formatWeightWithUnits(group.total) }}</span>
              <span v-if="group.cost.hasAnyPrice" class="caption ml-1">${{ formatDollars(group.cost.cost) }}</span>
              <ul>
                <li v-for="ingredient of group.ingredients" v-bind:key="`${ingredient.foodid}`">
                  {{ Number(ingredient.amount).toFixed(1) }}g {{ ingredient.name }}
                </li>
              </ul>
            </div>
          </v-col>
          <v-col cols="6">
            <template v-if="recipeLines.length>0 || componentNotes.length>0">
              <h3>Meal Recipe</h3>
              <div class="mt-3 mb-4">
                <div v-for="(line,i) of recipeLines" v-bind:key="`meal-${i}`">{{ line }}</div>
                <div v-for="group of componentNotes" v-bind:key="`notes-${group.key}`" class="mt-2">
                  <span class="caption">Notes for {{ group.name }}:</span>
                  <div v-for="(line,i) of group.notesLines" v-bind:key="i">{{ line }}</div>
                </div>
              </div>
            </template>
            <h3>Customer Instructions</h3>
            <ol class="mt-3">
              <li v-for="(instruction,i) of instructions" v-bind:key="i">{{ instruction }}</li>
            </ol>
            <p v-if="!loading && instructions.length===0" class="caption">No instructions.</p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api';
import moment from 'moment';
import {formatWeightWithUnits} from '@/store/utils';
import {formatDollars, ingredientCost} from '@/utils/componentIngredients';

const toLines = text => (text || '').split('\n').filter(line => line.trim());

export default {
  name: 'MealSummaryDialog',
  props: {
    value: {type: Boolean, default: false},
    mealId: {type: Number, required: false},
    mealName: {type: String, default: ''},
    dietName: {type: String, default: ''},
  },
  data() {
    return {
      loading: null,
      meal: {},
      ingredients: [],
      components: [],
      editingName: false,
      nameEdit: '',
      savingName: null,
      showHistory: false,
      loadingEdits: null,
      edits: [],
      preview: null,
      loadingPreview: null,
      restoringEditId: null,
    };
  },
  watch: {
    value(open) {
      if (open) {
        this.editingName = false;
        this.showHistory = false;
        this.preview = null;
        this.fetchMealSummary();
      }
    },
  },
  computed: {
    instructions() {
      return (this.meal.instructions || []).filter(i => !!i);
    },
    ingredientGroups() {
      const componentsById = Object.fromEntries(this.components.map(c => [c.id, c]));
      const groups = {};
      this.ingredients.forEach(ingredient => {
        const key = `${ingredient.component_id}`;
        const component = componentsById[ingredient.component_id] || {};
        groups[key] = groups[key] || {
          key,
          name: component.name || 'No component',
          notesLines: toLines(component.notes),
          total: 0,
          ingredients: [],
        };
        groups[key].total += ingredient.amount;
        groups[key].ingredients.push({
          foodid: ingredient.foodid,
          amount: ingredient.amount,
          name: ingredient.food_name.fooddescriptionsimple,
          price: ingredient.food_name.price,
        });
      });
      Object.values(groups).forEach(g => {
        g.ingredients.sort((a, b) => b.amount - a.amount);
        g.cost = ingredientCost(g.ingredients);
      });
      return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
    },
    componentNotes() {
      return this.ingredientGroups.filter(group => group.notesLines.length > 0);
    },
    recipeLines() {
      return toLines(this.meal.notes);
    },
    mealCost() {
      return ingredientCost(this.ingredients.map(i => ({
        amount: i.amount,
        price: i.food_name.price,
        name: i.food_name.fooddescriptionsimple,
      })));
    },
    // preview ingredients grouped by component, each marked against the current
    // recipe: same / changed amount / added back / removed by the restore
    previewGroups() {
      if (!this.preview) {
        return [];
      }
      const componentNames = Object.fromEntries(this.components.map(c => [c.id, c.name]));
      const currentByKey = {};
      this.ingredients.forEach(i => {
        currentByKey[`${i.component_id}_${i.foodid}`] = i;
      });
      const groups = {};
      const addToGroup = (name, ingredient) => {
        groups[name] = groups[name] || {name, total: 0, ingredients: []};
        if (ingredient.status !== 'removed') {
          groups[name].total += ingredient.amount;
        }
        groups[name].ingredients.push(ingredient);
      };
      const inPreview = new Set();
      this.preview.ingredients.forEach(ingredient => {
        const key = `${ingredient.component_id}_${ingredient.foodid}`;
        inPreview.add(key);
        const current = currentByKey[key];
        let status = 'same';
        let currentAmount = null;
        if (!current) {
          status = 'added';
        } else if (Math.abs(current.amount - ingredient.amount) > 0.05) {
          status = 'changed';
          currentAmount = current.amount;
        }
        addToGroup(ingredient.component_name, {...ingredient, status, currentAmount});
      });
      // current ingredients the restore would remove
      this.ingredients
        .filter(i => !inPreview.has(`${i.component_id}_${i.foodid}`))
        .forEach(i => addToGroup(componentNames[i.component_id] || 'No component', {
          foodid: i.foodid,
          name: i.food_name.fooddescriptionsimple,
          amount: i.amount,
          status: 'removed',
        }));
      Object.values(groups).forEach(g => g.ingredients.sort((a, b) => b.amount - a.amount));
      return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
    },
    previewInstructions() {
      if (!this.preview) {
        return [];
      }
      const current = new Set(this.instructions);
      const previewList = this.preview.meal.instructions
        .filter(text => !!text)
        .map(text => ({text, status: current.has(text) ? 'same' : 'changed'}));
      const previewSet = new Set(previewList.map(i => i.text));
      const removed = this.instructions
        .filter(text => !previewSet.has(text))
        .map(text => ({text, status: 'removed'}));
      return [...previewList, ...removed];
    },
    previewDateDiffers() {
      return this.preview
        && moment(this.preview.meal.date).format(moment.HTML5_FMT.DATE)
        !== moment(this.meal.date).format(moment.HTML5_FMT.DATE);
    },
    previewDescriptionDiffers() {
      return this.preview && (this.preview.meal.description || '') !== (this.meal.description || '');
    },
  },
  methods: {
    formatWeightWithUnits,
    formatDollars,
    startEditName() {
      this.nameEdit = this.meal.name || this.mealName;
      this.editingName = true;
    },
    async saveName() {
      const name = (this.nameEdit || '').trim();
      if (!name || name === this.meal.name) {
        this.editingName = false;
        return;
      }
      this.savingName = true;
      try {
        await api.put(`v2/meal/${this.mealId}`, {meal: {id: this.mealId, name}});
        this.meal = {...this.meal, name};
        this.editingName = false;
        this.$emit('renamed', {mealId: this.mealId, name});
      } catch (e) {
        console.error('failed to rename meal', e);
        alert(`failed to rename meal: ${e.message}`);
      } finally {
        this.savingName = false;
      }
    },
    formatDate(date) {
      return date ? moment(date).format('dddd MMMM D') : '';
    },
    formatEditDate(date) {
      return moment(date).format('MMM D HH:mm');
    },
    changeSummary(edit) {
      const changes = edit.changes || [];
      const fields = [...new Set(changes.map(c => `${(c.path || [])[0]}`))];
      const shown = fields.slice(0, 4).join(', ');
      const more = fields.length > 4 ? ` +${fields.length - 4} more` : '';
      return `${changes.length} change${changes.length === 1 ? '' : 's'}: ${shown}${more}`;
    },
    changeDetail(edit) {
      return (edit.changes || [])
        .slice(0, 20)
        .map(c => `${(c.path || []).join('.')}: ${JSON.stringify(c.oldValue)} -> ${JSON.stringify(c.value)}`)
        .join('\n');
    },
    toggleHistory() {
      this.showHistory = !this.showHistory;
      this.preview = null;
      if (this.showHistory) {
        this.fetchEdits();
      }
    },
    async openPreview(edit) {
      this.loadingPreview = edit.id;
      try {
        const {data} = await api.get(`v2/meal/${this.mealId}/edits/${edit.id}/preview`);
        this.preview = data;
      } catch (e) {
        console.error('failed to fetch restore preview', e);
        alert(`failed to fetch restore preview: ${e.message}`);
      } finally {
        this.loadingPreview = null;
      }
    },
    async confirmRestore() {
      const editId = this.preview.edit.id;
      this.restoringEditId = editId;
      try {
        await api.post(`v2/meal/${this.mealId}/restore/${editId}`);
        this.preview = null;
        this.showHistory = false;
        await this.fetchMealSummary();
        this.$emit('restored', {mealId: this.mealId});
      } catch (e) {
        console.error('failed to restore meal', e);
        alert(`failed to restore meal: ${e.message}`);
      } finally {
        this.restoringEditId = null;
      }
    },
    async fetchEdits() {
      this.loadingEdits = true;
      try {
        const {data} = await api.get(`v2/meal/${this.mealId}/edits`);
        this.edits = data;
      } catch (e) {
        console.error('failed to fetch meal edits', e);
        alert(`failed to fetch meal edits: ${e.message}`);
      } finally {
        this.loadingEdits = false;
      }
    },
    async fetchMealSummary() {
      this.loading = true;
      this.meal = {};
      this.ingredients = [];
      this.components = [];
      try {
        const [mealResponse, componentsResponse] = await Promise.all([
          api.get(`v2/meal/${this.mealId}`),
          api.get(`v2/meal/${this.mealId}/components`),
        ]);
        this.meal = mealResponse.data;
        this.ingredients = componentsResponse.data.ingredients;
        this.components = componentsResponse.data.components;
      } catch (e) {
        console.error('failed to fetch meal summary', e);
        alert(`failed to fetch meal summary: ${e.message}`);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>

</style>
